/**
 * Page d'accueil « Maison » — vue en deux moitiés égales :
 *  - Moitié gauche : Chat global + colonne « En ligne » (masquable manuellement ≥ 1500px)
 *  - Moitié droite : Toutes les tables
 *
 * Visible uniquement en mode wide (> 1024px).
 * Le chat global partage les mêmes composants que l'onglet Discussions (via AppContext).
 */

import { useEffect, useRef } from 'react';
import { useApp } from '../contexts/AppContext';
import { PlayerAvatar } from '../components/PlayerAvatar';
import ClickablePseudo from '../components/ClickablePseudo';
import GlobalChatFeed from '../components/chat/GlobalChatFeed';
import ChatInputComposer from '../components/chat/ChatInputComposer';
import { AllTablesList, isDiffIndivCategoryName } from './Tables';
import { useRetroDiffereTableInfo } from '../hooks/useRetroDiffereTableInfo';
import { DisconnectedIcon, SidebarCollapseIcon, SidebarRevealIcon } from '../components/Icons';
import type { Session } from '../types/app';
import type { GameTable } from '../types/game';
import '../styles/chat.css';
import '../styles/home.css';

interface HomeProps {
  session: Session;
}

export default function Home({ session }: HomeProps) {
  const {
    globalMessages,
    globalChatPanel,
    chatConnected,
    connectedUsers,
    playerTeams,
    tablesCategories,
    joinTable,
    openRetroDiffere,
    preferences,
    updatePreferences,
    setActiveTab,
    navigateToLibrary,
    navigateToDiffere,
  } = useApp();

  const onlineListVisible = preferences.homeOnlineListVisible;

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [globalMessages.length]);

  const handleSelectCategory = (categoryName: string) => {
    void categoryName;
    setActiveTab('tables');
  };

  const handleJoinFromHome = (table: GameTable) => {
    const draftTable = table as GameTable & { _isDraft?: boolean };
    if (draftTable._isDraft) {
      setActiveTab('tables');
    }
    joinTable?.(table);
  };

  const handleOpenRetroFromHome = () => {
    setActiveTab('tables');
    openRetroDiffere?.();
  };

  const hasRetroEntry = session.compet && tablesCategories.some(c =>
    isDiffIndivCategoryName(c.name) || c.tables.some(t => t.idserv === 'diff_indiv'),
  );
  const retroInfo = useRetroDiffereTableInfo(session.pseudo, !!hasRetroEntry);

  const totalStats = {
    tables: tablesCategories.reduce((sum, c) => sum + c.tables.length, 0) + (hasRetroEntry ? 1 : 0),
    players: tablesCategories.reduce((sum, c) => sum + c.playerCount, 0)
      + (hasRetroEntry ? retroInfo.activePlayerCount : 0),
  };

  const toggleOnlineList = () => {
    updatePreferences({ homeOnlineListVisible: !onlineListVisible });
  };

  return (
    <div
      className="home-container"
      data-online-list={onlineListVisible ? 'visible' : 'hidden'}
    >
      <div className="home-left">
        <section className="home-chat">
          <div className="home-column-header home-column-header--spread">
            <h2>Chat {session.compet ? 'Compétition' : 'Détente'}</h2>
            <button
              type="button"
              className="home-online-toggle home-online-toggle--reveal"
              title="Afficher les joueurs en ligne"
              aria-label={`Afficher les joueurs en ligne (${connectedUsers.length})`}
              onClick={toggleOnlineList}
            >
              <SidebarRevealIcon size={18} />
              <span>En ligne ({connectedUsers.length})</span>
            </button>
          </div>

          <div className="home-chat-messages messages-container">
            {globalChatPanel ? (
              <GlobalChatFeed
                messages={globalMessages}
                currentPseudo={session.pseudo}
                reactions={globalChatPanel.reactions}
                onToggleReaction={globalChatPanel.onToggleReaction}
                onReply={globalChatPanel.onReply}
                onRespondToGameInvite={globalChatPanel.onRespondToGameInvite}
                onCancelGameInvite={globalChatPanel.onCancelGameInvite}
                onAccessDraft={globalChatPanel.onAccessDraft}
                onOpenLibrary={navigateToLibrary}
                onOpenDiffere={navigateToDiffere}
                onScrollToQuote={globalChatPanel.onScrollToQuote}
                enableMessageShell
                messagesEndRef={messagesEndRef}
              />
            ) : (
              <div className="messages-empty">
                <p>💬</p>
                <p>Chargement du chat...</p>
              </div>
            )}
          </div>

          {globalChatPanel ? (
            <ChatInputComposer
              value={globalChatPanel.inputText}
              onChange={globalChatPanel.onInputChange}
              onSend={globalChatPanel.onSend}
              onKeyDown={globalChatPanel.onKeyDown}
              placeholder={globalChatPanel.replyingTo ? 'Votre réponse…' : 'Tapez un message...'}
              disabled={!globalChatPanel.connected}
              replyingTo={globalChatPanel.replyingTo}
              onDismissReply={globalChatPanel.onDismissReply}
              insertEmoji={globalChatPanel.insertEmoji}
              inputRef={globalChatPanel.inputRef}
              showAttachMenu={globalChatPanel.connected}
              onPoll={globalChatPanel.onPoll}
              onGameInvite={globalChatPanel.onGameInvite}
              gameInviteTitle={globalChatPanel.gameInviteTitle}
              gameInviteSubtitle={globalChatPanel.gameInviteSubtitle}
              gameInviteDisabled={globalChatPanel.gameInviteDisabled}
            />
          ) : (
            <div className="home-chat-input home-chat-input--loading">
              <span className="small">Connexion au chat...</span>
            </div>
          )}

          {!chatConnected && (
            <div className="home-disconnected">
              <DisconnectedIcon size={16} />
              <span>Connexion perdue</span>
            </div>
          )}
        </section>

        <aside className="home-users">
          <div className="home-column-header">
            <h2>En ligne ({connectedUsers.length})</h2>
            <button
              type="button"
              className="home-online-toggle home-online-toggle--icon home-online-toggle--collapse"
              title="Masquer les joueurs en ligne"
              aria-label="Masquer les joueurs en ligne"
              onClick={toggleOnlineList}
            >
              <SidebarCollapseIcon size={18} />
            </button>
          </div>
          <div className="home-users-list">
            {connectedUsers.map((user) => {
              const team = playerTeams[user.pseudo.toLowerCase()];
              return (
                <div key={user.pseudo} className="user-item">
                  <PlayerAvatar pseudo={user.pseudo} size={32} />
                  <div className="user-item-info">
                    <ClickablePseudo pseudo={user.pseudo} className="user-item-name">
                      {user.pseudoDisplay}
                    </ClickablePseudo>
                    {session.compet && team && (
                      <span className="user-item-team">{team}</span>
                    )}
                  </div>
                  <span className={`user-source ${user.source}`}>
                    {user.source === 'app' ? 'App' : 'IRC'}
                  </span>
                </div>
              );
            })}
          </div>
        </aside>
      </div>

      <section className="home-tables">
        <div className="home-column-header">
          <h2>Toutes les tables</h2>
          <span className="home-tables-stats">
            {totalStats.tables} table{totalStats.tables > 1 ? 's' : ''} · {totalStats.players} joueur{totalStats.players > 1 ? 's' : ''}
          </span>
        </div>
        <div className="home-tables-content">
          {joinTable ? (
            <AllTablesList
              sortedCategories={tablesCategories}
              onJoinTable={handleJoinFromHome}
              vintageMode={preferences.vintageTableIcons}
              currentPseudo={session.pseudo}
              onSelectCategory={handleSelectCategory}
              compet={session.compet}
              onOpenRetroDiffere={handleOpenRetroFromHome}
            />
          ) : (
            <div className="messages-empty">
              <p>🃏</p>
              <p>Chargement des tables...</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
